const firstValue = (...values) => values.find(
  (value) => value !== undefined && value !== null && value !== "",
);

const identityValues = (value) => new Set(
  [value?.id, value?.source_id, value?.release_node_id]
    .filter(Boolean)
    .map(String),
);

const identitiesOverlap = (left, right) => {
  const leftValues = identityValues(left);
  return [...identityValues(right)].some((value) => leftValues.has(value));
};

const projectAuthoritativeProductQuestions = (
  publishedQuestions = [],
  authority = null,
) => {
  const runtimeQuestions = (authority?.step?.programs || []).flatMap(
    (program) => program.checkout_questions || [],
  );

  return (publishedQuestions || []).flatMap((publishedQuestion) => {
    const runtimeQuestion = runtimeQuestions.find((candidate) => (
      identitiesOverlap(publishedQuestion, candidate)
    ));
    const runtimeOptions = Array.isArray(runtimeQuestion?.options)
      ? runtimeQuestion.options
      : [];
    if (!runtimeQuestion || runtimeOptions.length === 0) return [];

    const publishedOptions = (
      publishedQuestion.answer_choices
      || publishedQuestion.checkout_config?.products
      || []
    );
    const publishedByOptionId = new Map(
      publishedOptions
        .filter((option) => optionId(option))
        .map((option) => [optionId(option), option]),
    );
    const answerChoices = runtimeOptions.map((runtimeOption) => ({
      ...(publishedByOptionId.get(optionId(runtimeOption)) || {}),
      ...runtimeOption,
    }));

    return [{
      ...publishedQuestion,
      answer_choices: answerChoices,
      checkout_config: publishedQuestion.checkout_config
        ? {
            ...publishedQuestion.checkout_config,
            products: answerChoices,
          }
        : publishedQuestion.checkout_config,
    }];
  });
};

export const projectRuntimeQuestionnaireQuestions = (
  questions = [],
  authority = null,
) => {
  const authoritativeProgramLabs = (() => {
    if (authority?.state !== "lab_selection") return null;
    if (Array.isArray(authority?.step?.program_labs)) {
      return authority.step.program_labs;
    }

    // The runtime transition contract returns the active Lab requirements in
    // `step.programs[].labs`.  Older endpoints exposed the same projection as
    // `step.program_labs`.  Prefer either server projection over the authored
    // client copy: evaluating the latter again can incorrectly hide a Lab
    // that the runtime has already made current.
    const runtimePrograms = authority?.step?.programs;
    if (!Array.isArray(runtimePrograms)) return null;
    return runtimePrograms
      .filter((program) => Array.isArray(program?.labs))
      .map((program) => ({
        program_id: String(program.program_id || program.id || ""),
        program_name: program.program_name || program.name || "",
        labs: program.labs,
      }));
  })();
  const publishedProductQuestions = (questions || []).filter(
    (question) => question.question_type === "product_selection",
  );
  const projectedProducts = projectAuthoritativeProductQuestions(
    publishedProductQuestions,
    authority,
  );
  const projectedProductsById = new Map(
    projectedProducts.map((question) => [String(question.id), question]),
  );
  if (
    authority?.state === "product_selection"
    && projectedProducts.length === 0
  ) {
    const runtimeQuestions = (authority?.step?.programs || []).flatMap(
      (program) => program.checkout_questions || [],
    );
    const unavailableBoundary = publishedProductQuestions.find(
      (question) => runtimeQuestions.some(
        (runtimeQuestion) => identitiesOverlap(question, runtimeQuestion),
      ),
    );
    if (unavailableBoundary) {
      projectedProductsById.set(String(unavailableBoundary.id), {
        ...unavailableBoundary,
        answer_choices: [],
        runtime_availability: "unavailable",
      });
    }
  }

  return (questions || []).flatMap((question) => {
    if (question.question_type === "program_lab_checkout") {
      return [{
        ...question,
        ...(authoritativeProgramLabs
          ? {
              program_labs: authoritativeProgramLabs,
              // Runtime Lab visibility is evaluated by the server against the
              // immutable release. Do not run the authored rule again in the
              // browser: its question identities can differ from the
              // submitted runtime answer identities.
              runtime_authoritative_labs: true,
            }
          : {}),
        ...(authority?.state === "lab_selection"
          && authority?.step?.availability
          ? { runtime_availability: authority.step.availability }
          : {}),
      }];
    }
    if (question.question_type !== "product_selection") return [question];
    const projected = projectedProductsById.get(String(question.id));
    return projected ? [projected] : [];
  });
};

const numberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  return Number.isInteger(number) && number >= 0 ? number : null;
};

const optionId = (item) => String(
  item && typeof item === "object"
    ? item.option_id || item.optionId || ""
    : "",
);

const choiceInputIds = (item) => [
  item?.option_id,
  item?.optionId,
  item?.value,
  item?.id,
].filter((value) => value !== undefined && value !== null && value !== "")
  .map(String);

export const productSelectionChoiceKey = (item) => String(
  item && typeof item === "object"
    ? item.option_id
      || item.optionId
      || item.id
      || item.value
      || item.product_id
      || item.productId
      || ""
    : item || "",
);

const choiceFor = (item, choices) => {
  const ids = new Set(
    item && typeof item === "object"
      ? choiceInputIds(item)
      : [String(item || "")],
  );
  return (choices || []).find((choice) => (
    choiceInputIds(choice).some((id) => ids.has(id))
  )) || null;
};

// Mirrors product_selection_contract._product_identity on the server: stable
// catalog identity only. No name/label/description fallback -- two Products
// that merely share a label are not the same Product (decision flow 7.5), and
// the merged multi-treatment choice list makes such a collision reachable.
// Returns "" when nothing stable is present; callers must treat that as
// "unknown", never as a match.
const productIdentity = (item) => String(
  item?.clinical_group_key
  || item?.product_source_id
  || item?.source_product_id
  || item?.product_id
  || item?.productId
  || "",
).trim().toLowerCase();

const isPatientOrderable = (choice) => ![
  "clinician_only",
  "informational",
].includes(choice?.product_role);

const isCardinalityCounted = (choice) => [
  "primary_choice",
  "optional_addon",
].includes(choice?.product_role || "primary_choice");

export const productCategoryIdentity = (item) => {
  const categoryId = firstValue(
    item?.category_id,
    item?.categoryId,
    item?.category?.id,
  );
  if (categoryId !== undefined) return `id:${String(categoryId).trim()}`;
  const categoryName = firstValue(
    item?.category_name,
    item?.categoryName,
    item?.category?.name,
  );
  return categoryName
    ? `name:${String(categoryName).trim().toLowerCase()}`
    : "";
};

const productChoicesForSelectionQuestion = (question = {}) => {
  const choicesFor = (item) => (
    Array.isArray(item?.answer_choices)
      ? item.answer_choices
      : Array.isArray(item?.checkout_config?.products)
        ? item.checkout_config.products
        : []
  );
  const choices = [
    ...choicesFor(question),
    ...(Array.isArray(question.selection_group_questions)
      ? question.selection_group_questions.flatMap(choicesFor)
      : []),
  ];
  const seenOptionIds = new Set();
  return choices.filter((choice) => {
    const id = optionId(choice);
    if (!id) return true;
    if (seenOptionIds.has(id)) return false;
    seenOptionIds.add(id);
    return true;
  });
};

const productCategoryIdentities = (choices = []) => new Set(
  choices
    .filter(isCardinalityCounted)
    .map(productCategoryIdentity)
    .filter(Boolean),
);

const medicationCategoryConflict = (choices, selections) => {
  const requiredSupplyOptionIds = new Set(
    requiredSupplyGroups(choices)
      .flat()
      .filter((choice) => choice?.product_role === "required_companion")
      .map(optionId),
  );
  const selectedIds = new Set(selections.map(optionId));
  (choices || []).forEach((choice) => {
    if (
      choice?.product_role === "required_companion"
      && !requiredSupplyOptionIds.has(optionId(choice))
      && isAutoIncludedRequiredCompanion(choice, choices)
    ) {
      selectedIds.add(optionId(choice));
    }
  });

  const selectedByCategory = new Map();
  selectedIds.forEach((selectedId) => {
    const choice = choiceFor({ option_id: selectedId }, choices);
    if (!choice || !isPatientOrderable(choice)) return;
    const category = productCategoryIdentity(choice);
    if (!category) return;
    selectedByCategory.set(
      category,
      [...(selectedByCategory.get(category) || []), selectedId],
    );
  });
  return [...selectedByCategory.values()].some((optionIds) => optionIds.length > 1);
};

export const productSelectionCategoryError = (question, value) => {
  const choices = productChoicesForSelectionQuestion(question);
  const selections = normalizeProductSelections(value, choices);
  return medicationCategoryConflict(choices, selections)
    ? "Choose only one medication from each category. Review the conflicting selection; required medications remain included."
    : "";
};

export const productSelectionCategoryErrorAcrossQuestions = ({
  questions = [],
  responses = {},
  currentQuestions = [],
  currentResponse,
} = {}) => {
  const responseValues = (value) => (
    Array.isArray(value)
      ? value
      : value === null || value === undefined || value === ""
        ? []
        : [value]
  );
  const activeQuestionKeys = new Set(
    currentQuestions.flatMap((question) => [
      question?.id,
      question?.source_id,
      question?.release_node_id,
    ].filter((key) => key !== null && key !== undefined).map(String)),
  );
  const choices = [];
  const selections = [];
  let currentResponseAdded = false;

  (questions || []).forEach((question) => {
    const questionChoices = question?.answer_choices
      || question?.checkout_config?.products
      || [];
    choices.push(...questionChoices);

    const questionKeys = [
      question?.id,
      question?.source_id,
      question?.release_node_id,
    ].filter((key) => key !== null && key !== undefined).map(String);
    if (questionKeys.some((key) => activeQuestionKeys.has(key))) {
      if (!currentResponseAdded) {
        selections.push(...responseValues(currentResponse));
        currentResponseAdded = true;
      }
      return;
    }

    const responseKey = questionKeys.find((key) => (
      Object.prototype.hasOwnProperty.call(responses, key)
    ));
    if (responseKey) selections.push(...responseValues(responses[responseKey]));
  });

  if (!currentResponseAdded && currentQuestions.length) {
    selections.push(...responseValues(currentResponse));
  }

  return productSelectionCategoryError(
    { question_type: "product_selection", answer_choices: choices },
    selections,
  );
};
// A repeated entry for the same displayed Product can represent another
// duration/supply variant even when its authored role says required companion.
// Such variants are mutually exclusive UI choices and must not be auto-added.
const requiredCardKey = (choice) => {
  const group = String(choice?.choice_group || "").trim();
  if (group.startsWith("supply-")) {
    const scope = String(
      choice?.program_id
      || choice?.treatment_type_id
      || choice?.treatment_type_key
      || "",
    ).trim();
    return `supply:${scope}:${group}`;
  }
  return `option:${optionId(choice)}`;
};

export const categoryHasMultipleRequiredCards = (choice, choices = []) => {
  if (choice?.product_role !== "required_companion") return false;
  const category = productCategoryIdentity(choice);
  if (!category) return false;
  const keys = new Set();
  (choices || []).forEach((candidate) => {
    if (candidate?.product_role !== "required_companion") return;
    if (productCategoryIdentity(candidate) !== category) return;
    keys.add(requiredCardKey(candidate));
  });
  return keys.size > 1;
};

export const isAutoIncludedRequiredCompanion = (choice, choices = []) => {
  if (choice?.product_role !== "required_companion") return false;
  if (categoryHasMultipleRequiredCards(choice, choices)) return false;
  const identity = productIdentity(choice);
  // No stable identity to compare: treat the companion as genuinely required
  // rather than guessing it duplicates something else.
  if (!identity) return true;
  const treatmentTypeId = String(choice?.treatment_type_id || "");
  return !(choices || []).some((candidate) => (
    candidate !== choice
    && isPatientOrderable(candidate)
    // Scoped to one Treatment Type: the product-selection step merges the
    // choices of every selected Program into one list.
    && String(candidate?.treatment_type_id || "") === treatmentTypeId
    && productIdentity(candidate) === identity
  ));
};

const programTreatmentIdentity = (choice) => String(
  choice?.program_id
  || choice?.treatment_type_id
  || choice?.treatment_type_key
  || ""
).trim();

const supplyGroupName = (choice) => String(
  choice?.choice_group || "",
).trim();

// Required supply membership is independent from the patient-selectable
// cardinality contract. Keep every member in the group so mixed required /
// optional groups can be rendered and validated without letting an optional
// sibling replace the required duration.
export const requiredSupplyGroups = (choices = []) => {
  const groups = new Map();
  (choices || []).forEach((choice) => {
    const group = supplyGroupName(choice);
    if (!group.startsWith("supply-")) return;
    const key = `${programTreatmentIdentity(choice)}:${group}`;
    groups.set(key, [...(groups.get(key) || []), choice]);
  });
  return [...groups.values()].filter((group) => group.some(
    (choice) => choice?.product_role === "required_companion",
  ));
};

// Prefer an already-selected required duration, then fall back to authored
// order. Optional siblings are deliberately ignored in both cases.
export const requiredSupplyOptionFor = (group = [], selected = []) => {
  const selectedIds = new Set((Array.isArray(selected) ? selected : [selected])
    .map(optionId)
    .filter(Boolean));
  const required = group.filter(
    (choice) => choice?.product_role === "required_companion",
  );
  return required.find((choice) => selectedIds.has(optionId(choice)))
    || required[0]
    || null;
};

export const hasSelectedRequiredSupply = (group = [], selected = []) => {
  const selectedIds = new Set((Array.isArray(selected) ? selected : [selected])
    .map(optionId)
    .filter(Boolean));
  return group.some((choice) => (
    choice?.product_role === "required_companion"
    && selectedIds.has(optionId(choice))
  ));
};

// A supply group is any authored supply group containing a required row. Its
// required rows are mutually exclusive durations; optional siblings remain
// separate patient-selectable products.
export const isGroupedRequiredSupply = (choices = []) => {
  const groups = new Set(
    choices.map(supplyGroupName),
  );
  return choices.length > 0
    && groups.size === 1
    && [...groups][0].startsWith("supply-")
    && choices.some(
      (choice) => choice?.product_role === "required_companion",
    );
};

export const PRODUCT_SELECTION_FIELDS = Object.freeze([
  "option_id",
  "quantity",
]);

export const normalizeProductSelection = (item, choices = []) => {
  const choice = choiceFor(item, choices);
  const id = optionId(choice || (choices.length ? null : item));
  if (!id) return null;
  const configuredQuantity = firstValue(
    item?.quantity,
    choice?.quantity,
    choice?.default_quantity,
    1,
  );
  const quantity = Number(configuredQuantity);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  const configuredAllowedQuantities = (
    choice?.allowed_quantities
    ?? item?.allowed_quantities
    ?? [1]
  );
  const allowedQuantities = (Array.isArray(configuredAllowedQuantities)
    ? configuredAllowedQuantities
    : [configuredAllowedQuantities])
    .map((value) => Number(value))
    .filter((value) => Number.isInteger(value) && value > 0);
  const releasedQuantities = allowedQuantities.length ? allowedQuantities : [1];
  return {
    option_id: id,
    quantity: releasedQuantities.includes(quantity)
      ? quantity
      : releasedQuantities[0],
  };
};

export const normalizeProductSelections = (value, choices = []) => {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  const unique = new Map();
  values.forEach((item) => {
    const normalized = normalizeProductSelection(item, choices);
    if (normalized) unique.set(normalized.option_id, normalized);
  });
  return [...unique.values()];
};

export const partitionProductSelectionsByQuestion = (
  questions = [],
  value = [],
) => {
  const selected = Array.isArray(value) ? value : value ? [value] : [];
  return (questions || []).reduce((updates, question) => {
    const choices = Array.isArray(question?.answer_choices)
      ? question.answer_choices
      : Array.isArray(question?.checkout_config?.products)
        ? question.checkout_config.products
        : [];
    const allowedKeys = new Set(
      choices.map(productSelectionChoiceKey).filter(Boolean),
    );
    updates[question.id] = selected.filter((item) => (
      allowedKeys.has(productSelectionChoiceKey(item))
    ));
    return updates;
  }, {});
};

export const getProductSelectionLimits = (question = {}) => {
  const config = question.checkout_config || question.selection_config || {};
  const validation = question.validation || {};
  const groupedQuestions = Array.isArray(question.selection_group_questions)
    ? question.selection_group_questions
    : [];
  const groupedMinimums = groupedQuestions
    .map((item) => numberOrNull(firstValue(
      item.min_selections,
      item.checkout_config?.min_selections,
      item.validation?.min_selections,
    )))
    .filter((value) => value !== null);
  const groupedMaximums = groupedQuestions
    .map((item) => numberOrNull(firstValue(
      item.max_selections,
      item.checkout_config?.max_selections,
      item.validation?.max_selections,
    )))
    .filter((value) => value !== null);
  const configuredMaximum = numberOrNull(firstValue(
    question.max_selections,
    config.max_selections,
    validation.max_selections,
  )) ?? (groupedMaximums.length ? Math.min(...groupedMaximums) : null);
  const categoryCount = productCategoryIdentities(
    productChoicesForSelectionQuestion(question),
  ).size;
  return {
    minimum: numberOrNull(firstValue(
      question.min_selections,
      config.min_selections,
      validation.min_selections,
    )) ?? (groupedMinimums.length ? Math.max(...groupedMinimums) : null),
    // The category rule is the product-selection ceiling. Legacy authored
    // global maxima (often 1) must not block one selection from each category.
    maximum: categoryCount || configuredMaximum,
  };
};

export const groupedProductSelectionContract = (questions = []) => {
  const sourceQuestions = Array.isArray(questions) ? questions : [];
  const first = sourceQuestions[0] || {};
  const selectionGroupId = first.selection_group_id
    || first.checkout_config?.selection_group_id
    || null;
  const aggregate = {
    selection_group_id: selectionGroupId,
    selection_group_questions: sourceQuestions,
  };
  const limits = getProductSelectionLimits(aggregate);
  const hasMultipleMode = sourceQuestions.some(
    (item) => item.selection_mode === "multiple"
      || item.checkout_config?.selection_mode === "multiple",
  );
  const hasProductCategories = productCategoryIdentities(
    sourceQuestions.flatMap(productChoicesForSelectionQuestion),
  ).size > 0;
  const selectionMode = hasMultipleMode || hasProductCategories
    ? "multiple"
    : "single";
  return {
    ...aggregate,
    selection_mode: selectionMode,
    min_selections: limits.minimum,
    max_selections: limits.maximum,
    validation: {
      required: (limits.minimum || 0) > 0,
      min_selections: limits.minimum,
      max_selections: limits.maximum,
    },
  };
};

/**
 * Return the contiguous product-selection page around the active question.
 * Custom Programs intentionally combine every adjacent product question,
 * regardless of each source Program's authored selection group. Standalone
 * Programs retain their existing group boundaries.
 */
export const productSelectionGroupBounds = (
  questions = [],
  activeIndex = -1,
  { customProgram = false } = {},
) => {
  const current = questions[activeIndex];
  if (!current || current.question_type !== "product_selection") return null;

  const selectionGroupFor = (question) => question?.selection_group_id
    || question?.checkout_config?.selection_group_id
    || null;
  const activeSelectionGroup = selectionGroupFor(current);
  const belongsToPage = (question) => (
    question?.question_type === "product_selection"
    && (
      customProgram
      || !activeSelectionGroup
      || selectionGroupFor(question) === activeSelectionGroup
    )
  );

  let startIndex = activeIndex;
  let endIndex = activeIndex;
  while (startIndex > 0 && belongsToPage(questions[startIndex - 1])) {
    startIndex -= 1;
  }
  while (
    endIndex + 1 < questions.length
    && belongsToPage(questions[endIndex + 1])
  ) {
    endIndex += 1;
  }
  return { startIndex, endIndex };
};

export const isMultipleProductSelection = (question = {}) => {
  const limits = getProductSelectionLimits(question);
  return question.selection_mode === "multiple"
    || question.checkout_config?.selection_mode === "multiple"
    || question.selection_type === "multiple"
    || question.allow_multiple === true
    || question.selection_group_questions?.some(
      (item) => item.selection_mode === "multiple"
        || item.checkout_config?.selection_mode === "multiple",
    )
    || limits.minimum !== null
    || limits.maximum !== null;
};

export const productSelectionError = (question, value) => {
  const choices = productChoicesForSelectionQuestion(question);
  const selections = normalizeProductSelections(value, choices);
  const categoryError = productSelectionCategoryError(question, value);
  if (categoryError) return categoryError;
  if (!isMultipleProductSelection(question)) return "";
  const count = selections
    .filter((item) => isCardinalityCounted(choiceFor(item, choices) || item))
    .length;
  const selectedByCategory = new Set();
  for (const item of selections) {
    const choice = choiceFor(item, choices) || item;
    if (!isCardinalityCounted(choice)) continue;
    const category = productCategoryIdentity(choice);
    if (!category) continue;
    if (selectedByCategory.has(category)) {
      return "Choose only one Product from each category.";
    }
    selectedByCategory.add(category);
  }
  const requiredPrimaryCategories = new Set(
    choices
      .filter((choice) => (
        (choice?.product_role || "primary_choice") === "primary_choice"
      ))
      .map(productCategoryIdentity)
      .filter(Boolean),
  );
  const selectedPrimaryCategories = new Set(
    selections
      .map((item) => choiceFor(item, choices) || item)
      .filter((choice) => (
        (choice?.product_role || "primary_choice") === "primary_choice"
      ))
      .map(productCategoryIdentity)
      .filter(Boolean),
  );
  if ([...requiredPrimaryCategories].some(
    (category) => !selectedPrimaryCategories.has(category),
  )) {
    return "Select one product from each required category to continue.";
  }
  const limits = getProductSelectionLimits(question);
  const hasPrimaryChoice = choices.some(
    (choice) => (choice?.product_role || "primary_choice") === "primary_choice",
  );
  const autoIncludedRequiredCompanions = choices.filter((choice) => (
    isAutoIncludedRequiredCompanion(choice, choices)
  ));
  const selectedOptionIds = new Set(selections.map((item) => optionId(item)));
  const requiredSupplyGroupList = requiredSupplyGroups(choices);
  const invalidRequiredSupply = requiredSupplyGroupList.some((group) => (
    group.filter((choice) => (
      choice?.product_role === "required_companion"
      && selectedOptionIds.has(optionId(choice))
    )).length > 1
  ));
  if (invalidRequiredSupply) {
    return "Select only one required supply option to continue.";
  }
  const missingRequiredSupply = requiredSupplyGroupList.some((group) => {
    const anchor = group.find((choice) => choice?.product_role === "required_companion") || group[0];
    if (anchor && categoryHasMultipleRequiredCards(anchor, choices)) {
      const category = productCategoryIdentity(anchor);
      return !selections.some((item) => {
        const selectedChoice = choiceFor(item, choices) || item;
        return selectedChoice?.product_role === "required_companion"
          && productCategoryIdentity(selectedChoice) === category;
      });
    }
    return !hasSelectedRequiredSupply(group, selections);
  });
  if (missingRequiredSupply) {
    return "Select at least 1 Product, including the required supply, to continue.";
  }
  const groupedRequiredSupplySelected = requiredSupplyGroupList.some((group) => (
    hasSelectedRequiredSupply(group, selections)
  ));
  // Several Required cards in one category are alternatives, so choosing any
  // one of them is the required selection, not only a supply-group card.
  const alternativeRequiredSelected = selections.some((item) => {
    const choice = choiceFor(item, choices);
    return choice?.product_role === "required_companion"
      && categoryHasMultipleRequiredCards(choice, choices);
  });
  const allRequiredCompanionsSelected = (
    autoIncludedRequiredCompanions.length > 0
    && autoIncludedRequiredCompanions.every((choice) => (
      selectedOptionIds.has(optionId(choice))
    ))
  );
  // Required companions are already included in the order. If a question has
  // no primary choice, an authored minimum of one must not force the patient
  // to select an optional add-on just to continue.
  const requiredOnlySupplySatisfiesMinimum = (
    !hasPrimaryChoice
    && (
      allRequiredCompanionsSelected
      || groupedRequiredSupplySelected
      || alternativeRequiredSelected
    )
  );
  const minimum = requiredOnlySupplySatisfiesMinimum
    ? 0
    : limits.minimum ?? (question.validation?.required ? 1 : 0);
  if (count < minimum) {
    return `Select at least ${minimum} Product${minimum === 1 ? "" : "s"} to continue.`;
  }
  if (limits.maximum !== null && count > limits.maximum) {
    return `Select no more than ${limits.maximum} Product${limits.maximum === 1 ? "" : "s"}.`;
  }
  return "";
};

export const isProductSelectionComplete = (question, value) => (
  !productSelectionError(question, value)
);

const CHILD_SUCCESS_STATUSES = new Set(["authorized", "captured"]);
const FAILURE_STATUSES = new Set([
  "declined",
  "failed",
  "payment_failed",
  "payment_pending",
  "unknown",
]);
const PRODUCT_FAILURE_STATUSES = new Set([
  "cancelled",
  "canceled",
  "declined",
  "failed",
  "payment_failed",
  "voided",
]);
const PRODUCT_UNKNOWN_STATUSES = new Set([
  "expired",
  "reconciliation_required",
  "unknown",
]);
const PRODUCT_SUCCESS_STATUSES = new Set(["authorized", "captured"]);

export const normalizeCombinedCheckoutResult = (result) => ({
  ...(result || {}),
  orders: Array.isArray(result?.orders) ? result.orders : [],
});

export const productPaymentStatus = (payment) => String(
  payment?.status || "pending",
).trim().toLowerCase();

export const isProductPaymentSuccessful = (payment) => (
  PRODUCT_SUCCESS_STATUSES.has(productPaymentStatus(payment))
);

export const productPaymentState = (payment) => {
  const status = productPaymentStatus(payment);
  if (PRODUCT_FAILURE_STATUSES.has(status)) return "failed";
  if (PRODUCT_UNKNOWN_STATUSES.has(status)) return "unknown";
  if (PRODUCT_SUCCESS_STATUSES.has(status)) return "ready";
  return "waiting";
};

export const isCombinedCheckoutSuccessful = (result) => {
  const normalized = normalizeCombinedCheckoutResult(result);
  if (!normalized.orders.length) return false;
  return normalized.orders.every((order) => {
    const productPayments = Array.isArray(order?.product_payments)
      ? order.product_payments
      : null;
    // Legacy order responses do not contain product-level rows. Preserve
    // their established success contract while enforcing every reservation
    // whenever the product-level contract is present.
    if (productPayments?.length && !productPayments.every(isProductPaymentSuccessful)) {
      return false;
    }
    const childStatus = String(order?.child_payment?.status || "").toLowerCase();
    const caseStatus = String(order?.status || "").toLowerCase();
    return CHILD_SUCCESS_STATUSES.has(childStatus)
      && !FAILURE_STATUSES.has(caseStatus)
      && !FAILURE_STATUSES.has(String(order?.b2b_authorization?.status || "").toLowerCase())
      && !FAILURE_STATUSES.has(String(order?.beluga_dispatch?.status || "").toLowerCase());
  });
};

export const assertCombinedCheckoutSuccess = (result) => {
  if (isCombinedCheckoutSuccessful(result)) return result;
  const error = new Error("Payment authorization is still being reconciled. Please try again later.");
  error.code = "payment_pending";
  error.retryable = true;
  error.checkoutRecovery = result?.checkout_recovery || null;
  throw error;
};
